import {
  getUserProfile,
  putUserProfile,
  updateUserReservation,
  upgradeUserReservation,
  updateUserReservationWaitlist,
} from '@api/api';
import {
  validateToken,
  removeAccessToken,
  composeRenameConfiguration,
  composeRemoveConfiguration,
  composeUpdateReservation,
  composeUpgradeReservation,
  composeJoinWaitlist,
} from '@utils';
import {
  profileLoading,
  profileSuccess,
  profileFailure,
  profileReset,
} from './userProfileSlice';
import { appLogout, displayModal } from '../appStateSlice';
import { documentsSuccess, documentsReset } from '../documentsSlice';

export const fetchProfile = () => async (dispatch, getState) => {
  const { appState, userProfile } = getState();
  const { isProfileLoading } = userProfile;

  if (isProfileLoading) {
    return;
  }

  const { token } = appState;
  const validToken = validateToken(token);

  if (!validToken) {
    throw new Error(`invalid session`);
  }

  dispatch(profileLoading());
  const { data: profileData, documents } = await getUserProfile(token);
  dispatch(profileSuccess(profileData.data));
  dispatch(documentsSuccess(documents));
};

export const renameConfiguration =
  (nextName, configurationData) => async (dispatch, getState) => {
    try {
      const { appState } = getState();
      const { token } = appState;
      const validToken = validateToken(token);

      if (!validToken) {
        throw new Error(`invalid session`);
      }

      const requestData = composeRenameConfiguration(
        nextName,
        configurationData
      );
      const { status, data: profileData } = await putUserProfile(
        token,
        requestData
      );

      if (status !== 200) {
        throw new Error(`renaming configuration`);
      }

      dispatch(profileSuccess(profileData.data));
    } catch (err) {
      if (err?.response?.status === 401 || err?.message === 'invalid session') {
        removeAccessToken();
        dispatch(appLogout());
        dispatch(profileReset());
        dispatch(documentsReset());
      } else {
        dispatch(profileFailure());
      }
    }
  };

export const removeConfiguration =
  configurationData => async (dispatch, getState) => {
    try {
      const { appState } = getState();
      const { token } = appState;
      const validToken = validateToken(token);

      if (!validToken) {
        throw new Error(`invalid session`);
      }

      const requestData = composeRemoveConfiguration(configurationData);
      dispatch(profileLoading());
      const { status, data: profileData } = await putUserProfile(
        token,
        requestData
      );

      if (status !== 200) {
        throw new Error(`removing configuration`);
      }

      dispatch(profileSuccess(profileData.data));
    } catch (err) {
      if (err?.response?.status === 401 || err?.message === 'invalid session') {
        removeAccessToken();
        dispatch(appLogout());
        dispatch(profileReset());
        dispatch(documentsReset());
      } else {
        dispatch(profileFailure());
      }
    }
  };

export const changeReservation =
  (configurationData, nextConfiguration) => async (dispatch, getState) => {
    try {
      const { appState } = getState();
      const { token } = appState;
      const validToken = validateToken(token);

      if (!validToken) {
        throw new Error(`invalid session`);
      }

      const requestData = composeUpdateReservation(
        configurationData,
        nextConfiguration
      );
      dispatch(profileLoading());
      const { status, data: profileData } = await updateUserReservation(
        token,
        requestData
      );

      if (status !== 200) {
        throw new Error(`changing configuration`);
      }

      // TODO: should update to use new api version - profileData.data
      dispatch(profileSuccess(profileData));
    } catch (err) {
      if (err?.response?.status === 401 || err?.message === 'invalid session') {
        removeAccessToken();
        dispatch(appLogout());
        dispatch(profileReset());
        dispatch(documentsReset());
      } else {
        dispatch(profileFailure());
      }
    }
  };

// Upgrade to a Trim with no configuration
// Upgrade to a Trim with with configuration
export const upgradeReservation = options => async (dispatch, getState) => {
  try {
    const { appState } = getState();
    const { token } = appState;
    const validToken = validateToken(token);

    if (!validToken) {
      throw new Error(`invalid session`);
    }

    const configurationData = options.configurationData;
    const stripeResult = options.stripeResult;
    const trim = options.trim;
    let dreamWaitlist = options.dreamWaitlist || false;
    let paymentDetails = null;

    if (stripeResult) {
      paymentDetails = {
        transactionId: stripeResult.paymentIntent.id,
        transactionDate: new Date(
          stripeResult.paymentIntent.created * 1000
        ).toISOString(),
      };
    }

    const requestData = composeUpgradeReservation({
      configurationData,
      trim: trim,
      paymentDetails: paymentDetails,
      dreamWaitlist: dreamWaitlist,
    });
    dispatch(profileLoading());
    const { status, data: profileData } = await upgradeUserReservation(
      token,
      requestData
    );

    if (status !== 200) {
      throw new Error(`upgrading reservation`);
    }

    dispatch(profileSuccess(profileData.data));
    dispatch(
      displayModal({
        modalName: 'PAYMENT_SUCCESS',
        modalProps: {
          trim: trim,
        },
      })
    );
  } catch (err) {
    if (err?.response?.status === 401 || err?.message === 'invalid session') {
      removeAccessToken();
      dispatch(appLogout());
      dispatch(profileReset());
      dispatch(documentsReset());
    } else {
      dispatch(profileFailure());
      dispatch(
        displayModal({
          modalName: 'PAYMENT_ERROR',
          modalProps: {
            configurationData: options.configurationData,
          },
        })
      );
    }
  }
};

export const upgradeError = options => async (dispatch, getState) => {
  try {
    const { appState } = getState();
    const { token } = appState;
    const validToken = validateToken(token);

    if (!validToken) {
      throw new Error(`invalid session`);
    }

    const configurationData = options.configurationData;
    const errorResult = options.stripeResult;
    const trim = options.trim;
    let dreamWaitlist = options.dreamWaitlist || false;
    let paymentDetails = null;

    if (errorResult) {
      paymentDetails = {
        transactionId: errorResult.payment_intent.id,
        transactionDate: new Date(
          errorResult.payment_intent.created * 1000
        ).toISOString(),
        status: 'failed',
      };
    }

    const requestData = composeUpgradeReservation({
      configurationData: configurationData,
      trim: trim,
      paymentDetails: paymentDetails,
      dreamWaitlist: dreamWaitlist,
    });
    const { status } = await upgradeUserReservation(token, requestData);

    if (status !== 200) {
      throw new Error(`upgrading reservation`);
    }

    // Do nothing
  } catch (err) {
    if (err?.response?.status === 401 || err?.message === 'invalid session') {
      removeAccessToken();
      dispatch(appLogout());
      dispatch(profileReset());
      dispatch(documentsReset());
    } else {
      dispatch(profileFailure());
      dispatch(
        displayModal({
          modalName: 'PAYMENT_ERROR',
          modalProps: {
            configurationData: options.configurationData,
          },
        })
      );
    }
  }
};

export const updateWaitlist =
  configurationData => async (dispatch, getState) => {
    try {
      const { appState } = getState();
      const { token } = appState;
      const validToken = validateToken(token);

      if (!validToken) {
        throw new Error(`invalid session`);
      }

      const trim = configurationData.trim;

      const requestData = composeJoinWaitlist(configurationData);
      dispatch(profileLoading());
      const { status, data: profileData } = await updateUserReservationWaitlist(
        token,
        requestData
      );

      if (status !== 200) {
        throw new Error(`updating waitlist on reservation`);
      }

      dispatch(profileSuccess(profileData.data));
      dispatch(
        displayModal({
          modalName: 'PAYMENT_SUCCESS',
          modalProps: {
            trim: trim,
            waitlist: true,
          },
        })
      );
    } catch (err) {
      if (err?.response?.status === 401 || err?.message === 'invalid session') {
        removeAccessToken();
        dispatch(appLogout());
        dispatch(profileReset());
        dispatch(documentsReset());
      } else {
        dispatch(profileFailure());
      }
    }
  };
